import type {
  AppSpec,
  AppStruct,
  NotificationIntegrationStruct,
  PerkIntegrationSpec,
  PerkIntegrationStruct,
  TriggerIntegrationSpec,
  TriggerIntegrationStruct,
} from '@cohort/shared/schema/apps/app';
import {CohortAppSpec} from '@cohort/shared/schema/apps/cohort';
import {DiscordAppSpec} from '@cohort/shared/schema/apps/discord';
import {GarminConnectAppSpec} from '@cohort/shared/schema/apps/garmin-connect';
import {GoogleFitAppSpec} from '@cohort/shared/schema/apps/google-fit';
import {InstagramAppSpec} from '@cohort/shared/schema/apps/instagram';
import {PostgresqlAppSpec} from '@cohort/shared/schema/apps/postgresql';
import {PrestashopAppSpec} from '@cohort/shared/schema/apps/prestashop';
import {ShopifyAppSpec} from '@cohort/shared/schema/apps/shopify';
import {StravaAppSpec} from '@cohort/shared/schema/apps/strava';
import {TwitterAppSpec} from '@cohort/shared/schema/apps/twitter';
import {TypeformAppSpec} from '@cohort/shared/schema/apps/typeform';
import type {UserEventStruct} from '@cohort/shared/schema/apps/userEvent';
import {YoutubeAppSpec} from '@cohort/shared/schema/apps/youtube';
import {CohortError} from '@cohort/shared/schema/common/errors';
import {compact, flatten} from 'remeda';
import {z} from 'zod';

// Get a list of all Apps perkIntegrationSpecs and a type for their ids
const apps = [
  CohortAppSpec,
  DiscordAppSpec,
  GarminConnectAppSpec,
  GoogleFitAppSpec,
  InstagramAppSpec,
  PostgresqlAppSpec,
  PrestashopAppSpec,
  ShopifyAppSpec,
  StravaAppSpec,
  TwitterAppSpec,
  TypeformAppSpec,
  YoutubeAppSpec,
] as const satisfies ReadonlyArray<AppSpec>;

export function getAppSpecs(): readonly AppSpec[] {
  return apps;
}

// AppId
export type AppId = (typeof apps)[number]['id'];
const appIds = apps.map(app => app.id) as unknown as readonly [AppId, ...AppId[]];
export const AppIdSchema = z.enum(appIds);

// EventId
export type EventId = (typeof apps)[number]['userEventSpecs'][number]['id'];
const eventIds = apps.flatMap(app =>
  app.userEventSpecs.map(event => event.id)
) as unknown as readonly [EventId, ...EventId[]];
export const EventIdSchema = z.enum(eventIds);

// NotificationIntegrationId
export type NotificationIntegrationId =
  (typeof apps)[number]['notificationIntegrationSpecs'][number]['id'];
const notificationIntegrationIds = apps.flatMap(app =>
  app.notificationIntegrationSpecs.map(notificationIntegration => notificationIntegration.id)
) as unknown as readonly [NotificationIntegrationId, ...NotificationIntegrationId[]];
export const NotificationIntegrationIdSchema = z.enum(notificationIntegrationIds);

// ConnectorId
export type MerchantConnectorId = NonNullable<(typeof apps)[number]['merchantConnector']>['id'];
export type UserConnectorId = NonNullable<(typeof apps)[number]['userConnector']>['id'];
export type ConnectorId = MerchantConnectorId | UserConnectorId;
const connectorIds = apps
  .flatMap(app => [app.merchantConnector?.id, app.userConnector?.id])
  .filter(Boolean) as unknown as readonly [ConnectorId, ...ConnectorId[]];
export const ConnectorIdSchema = z.enum(connectorIds);

export type PerkIntegrationId = (typeof apps)[number]['perkIntegrationSpecs'][number]['id'];
const perkIntegrationIds = apps.flatMap(app =>
  app.perkIntegrationSpecs.map(perkIntegration => perkIntegration.id)
) as unknown as readonly [PerkIntegrationId, ...PerkIntegrationId[]];
export const PerkIntegrationIdSchema = z.enum(perkIntegrationIds);

export type TriggerIntegrationId = (typeof apps)[number]['triggerIntegrationSpecs'][number]['id'];
const triggerIntegrationIds = apps.flatMap(app =>
  app.triggerIntegrationSpecs.map(triggerIntegration => triggerIntegration.id)
) as unknown as readonly [TriggerIntegrationId, ...TriggerIntegrationId[]];
export const TriggerIntegrationIdSchema = z.enum(triggerIntegrationIds);

export function getAppSpec<T extends AppStruct = AppStruct>(appId: T['Id']): AppSpec<T> {
  const app = apps.find((app: AppSpec) => app.id === appId);
  if (!app) {
    throw new CohortError('app.not-found', {appId});
  }
  return app as unknown as AppSpec<T>;
}

export function getPerkIntegrationSpecs(): readonly PerkIntegrationSpec[] {
  const appsSpecs = getAppSpecs();
  const perkIntegrationSpecs = compact(appsSpecs.flatMap(app => app.perkIntegrationSpecs));

  return perkIntegrationSpecs;
}

export function getTriggerIntegrationSpecs(): readonly TriggerIntegrationSpec[] {
  const appsSpecs = getAppSpecs();
  const triggerIntegrationSpecs = compact(appsSpecs.flatMap(app => app.triggerIntegrationSpecs));

  return triggerIntegrationSpecs;
}

export function getPerkIntegrationSpec<T extends PerkIntegrationStruct>(
  perkIntegrationId: T['Id']
): PerkIntegrationSpec<T> {
  const perkIntegrationSpecs = getPerkIntegrationSpecs();
  const perkIntegration = perkIntegrationSpecs.find(
    perkIntegration => perkIntegration.id === perkIntegrationId
  );

  if (!perkIntegration) {
    throw new CohortError('perk-integration.not-found', {perkIntegrationId});
  }
  return perkIntegration as unknown as PerkIntegrationSpec<T>;
}

export function getTriggerIntegrationSpec<T extends TriggerIntegrationStruct>(
  triggerIntegrationId: T['Id']
): TriggerIntegrationSpec<T> {
  const triggerIntegrationSpecs = getTriggerIntegrationSpecs();
  const triggerIntegration = triggerIntegrationSpecs.find(
    triggerIntegration => triggerIntegration.id === triggerIntegrationId
  );

  if (!triggerIntegration) {
    throw new CohortError('trigger-integration.not-found', {triggerIntegrationId});
  }
  return triggerIntegration as unknown as TriggerIntegrationSpec<T>;
}

export function getNotificationIntegrationSpec<T extends NotificationIntegrationStruct>(
  notificationIntegrationId: T['Id']
): AppSpec['notificationIntegrationSpecs'][number] {
  const appsSpecs = getAppSpecs();
  const notificationIntegrationSpecs = compact(
    appsSpecs.flatMap(app => app.notificationIntegrationSpecs)
  );
  const notificationIntegration = notificationIntegrationSpecs.find(
    notificationIntegration => notificationIntegration.id === notificationIntegrationId
  );

  if (!notificationIntegration) {
    throw new CohortError('notification-integration.not-found', {notificationIntegrationId});
  }
  return notificationIntegration;
}

export function getUserEventSpec<T extends UserEventStruct>(
  eventId: T['Id']
): AppSpec['userEventSpecs'][number] {
  const eventSpecs = flatten(apps.map(app => app.userEventSpecs));
  const userEvent = eventSpecs.find(event => event.id === eventId);
  if (!userEvent) {
    throw new CohortError('user-event.not-found', {eventId});
  }
  return userEvent;
}
